import React, { Fragment } from 'react';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Tooltip, DatePickerFormItem, Icon, fulfilledCssClass } from '@jjj/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ImageCellView from './imageCell';
import { withTZ, getDateFormat } from '../../helpers/dateTime';
import { t } from '../../i18n';
import CellsWrapper, {
  ItemsCount,
  TapToCopyCellWrapper,
  TooltipItem,
  TooltipItemHeaderLabel,
  TruncatedItems,
  ExternalLink,
  StyledLink,
} from './cells.style';
import { formatCurrency } from '../../helpers/countriesHelpers';

const notAvailable = t('dash.common.aliases.n/a');

const ifNumberIsNull = number => {
  return number === null ? 0 : number;
};

const InputDateTimePickerFormItemCell = ({
  value,
  onChange,
  showTime,
  noLocalTimeZone,
  timezone,
  help,
  validateStatus,
  disabledDate,
  disabled,
  allowClear,
}) => {
  let timeMoment;

  if (value) {
    if (noLocalTimeZone) {
      timeMoment = moment(value);
    } else if (timezone) {
      timeMoment = moment.tz(value, timezone);
    } else {
      timeMoment = withTZ(value);
    }
  }

  return (
    <CellsWrapper>
      <div className="input-form-item-cell">
        <DatePickerFormItem
          disabledDate={disabledDate}
          showTime={showTime}
          validateStatus={validateStatus}
          help={help}
          value={timeMoment}
          dateTimeFormat={getDateFormat('datetime_date_numeric')}
          onChange={onChange}
          disabled={disabled}
          allowClear={allowClear}
          timezone={timezone}
        />
      </div>
    </CellsWrapper>
  );
};

const CustomLink = ({ to, children }) => {
  if (!to || typeof to !== 'string') {
    return <div className="text-cell-text-wrapper">{children}</div>;
  }

  return (
    <Link className="link-to-new-page" to={to.startsWith('http') ? { pathname: to } : to} data-cy="tables-custom_link">
      {children}
    </Link>
  );
};

const ImageCell = (src, href) => (
  <CustomLink to={href || '#'}>
    <ImageCellView src={src} />
  </CustomLink>
);

const LinkCell = (link, href) => {
  return <CustomLink to={href || '#'}>{link}</CustomLink>;
};

const StyledLinkCell = (text, href) => {
  if (!href) {
    return <span>{text}</span>;
  }

  return <StyledLink to={href}>{text}</StyledLink>;
};

// An anchor with no `href` gets no role, no tab stop and no Enter handling, hence role/tabIndex/onKeyDown.
// A disabled cell keeps its tab stop: `aria-disabled` announces it, `pointer-events: none` refuses the mouse.
export const OnClickCell = (label, onClick, disabled, dataCy) => {
  const activate = event => {
    if (disabled || (event.key !== 'Enter' && event.key !== ' ')) return;
    // Space scrolls the page and Enter submits an enclosing form; neither belongs to activating a cell.
    event.preventDefault();
    onClick?.(event);
  };

  return (
    <ExternalLink
      role="button"
      tabIndex={0}
      aria-disabled={disabled || undefined}
      onClick={disabled ? undefined : onClick}
      onKeyDown={activate}
      $disabled={disabled}
      data-cy={dataCy}
    >
      {label}
    </ExternalLink>
  );
};

const TextCell = (text, href, inactive, align, width, direction) => (
  <CellsWrapper $align={align} $width={width} $direction={direction}>
    <CustomLink to={href}>
      <span className={inactive ? 'inactive-cell' : ''}>{text}</span>
    </CustomLink>
  </CellsWrapper>
);

const TextCellWithHoverMoreTooltip = (itemsArray, href, headerLabel, visibleItemsCount = 2, inactive) => {
  let intemsInTooltip = [];
  let items = itemsArray;
  const shouldBeTruncated = itemsArray && Array.isArray(itemsArray) && itemsArray.length > visibleItemsCount;

  if (shouldBeTruncated) {
    items = itemsArray.slice(0, visibleItemsCount);
    intemsInTooltip = itemsArray.slice(visibleItemsCount);
  } else if (!Array.isArray(itemsArray)) {
    items = [itemsArray];
  }

  const renderItemsInTooltip = array => {
    return (
      <Fragment>
        <TooltipItemHeaderLabel>{headerLabel}</TooltipItemHeaderLabel>
        {array.map(item => (
          <TooltipItem key={item}>{item}</TooltipItem>
        ))}
      </Fragment>
    );
  };

  return (
    <CellsWrapper>
      <CustomLink to={href}>
        <span className={inactive ? 'inactive-cell hover-more-tooltip-wrapper' : 'hover-more-tooltip-wrapper'}>
          {items && items.length > 0 && (
            <TruncatedItems showTruncatedChar={shouldBeTruncated || undefined}>
              {items.map((item, index) => (index ? ', ' : '') + item)}
            </TruncatedItems>
          )}
          {intemsInTooltip.length > 0 && (
            <Tooltip placement="rightTop" title={renderItemsInTooltip(intemsInTooltip)}>
              <ItemsCount>+{intemsInTooltip.length}</ItemsCount>
            </Tooltip>
          )}
        </span>
      </CustomLink>
    </CellsWrapper>
  );
};

const TextCellWithTag = (text, href, tagText, inactive = false) => {
  return (
    <CellsWrapper>
      <div className="text-cell-with-tag">
        {TextCell(text, href, inactive, undefined, 'auto')}
        {tagText && <span className="tag">{tagText}</span>}
      </div>
    </CellsWrapper>
  );
};

const IndicatorCell = (field, url) =>
  TextCell(
    field ? (
      <span>
        On <i className="far fa-check-circle" />
      </span>
    ) : (
      <span>
        Off <i className="far fa-times-circle" />
      </span>
    ),
    url,
  );

const TooltipCell = (text, title, placement = 'bottom') => (
  <CellsWrapper>
    <Tooltip placement={placement} title={title}>
      {text}
    </Tooltip>
  </CellsWrapper>
);

const IconCell = ({
  icon,
  hoverText = '',
  text = '',
  style,
  position = 'left',
  isFontAwesomeIcon = false,
  inactive = false,
}) => {
  const isIconLeft = position === 'left';
  const content = (
    <span className={`custom-cell-icon-text custom-cell-icon-text-${position}${inactive ? ' inactive-cell' : ''}`}>
      {text}
    </span>
  );

  return icon ? (
    <CellsWrapper>
      <div className="custom-cell-icon-wrapper">
        {text && !isIconLeft ? content : null}
        <Tooltip placement="bottom" title={hoverText}>
          <div className="custom-cell-icon-tooltip-content">
            <div className="custom-cell-icon" style={style}>
              {isFontAwesomeIcon ? <FontAwesomeIcon icon={icon} /> : <Icon type={icon} theme="filled" />}
            </div>
          </div>
        </Tooltip>
        {text && isIconLeft ? content : null}
      </div>
    </CellsWrapper>
  ) : (
    TextCell('-')
  );
};

const NumberCell = (value, postfix = '') => TextCell(typeof value === 'number' ? value + postfix : 'N/A');

const TextCellWithHover = (paragraphText, hoverText, href) => (
  <CellsWrapper withHover={hoverText || undefined} className={hoverText ? 'with-hover' : ''}>
    {/* Beside the link, not inside it: link content would join the row link's accessible name. The tag keeps the
        global heading styles; role="none" keeps the text from listing as a heading per row. */}
    <h5 className="hover-header" role="none">
      {hoverText}
    </h5>
    <CustomLink to={href || ''}>
      <p>{paragraphText}</p>
    </CustomLink>
  </CellsWrapper>
);

const TooltipCellWithHover = (text, title, hoverText, href, placement = 'bottom') => (
  <Tooltip placement={placement} title={title}>
    <CellsWrapper withHover={hoverText || undefined} className={hoverText ? 'with-hover' : ''}>
      <h5 className="hover-header" role="none">
        {hoverText}
      </h5>
      <CustomLink to={href || ''}>
        <p>{text}</p>
      </CustomLink>
    </CellsWrapper>
  </Tooltip>
);

const CurrencyCell = (value, href, info) => (
  <CellsWrapper>
    <CustomLink to={href || ''}>
      <p style={{ marginBottom: 0 }}>
        {formatCurrency(value)}{' '}
        {!!info && (
          <Tooltip placement="bottom" title={info}>
            <Icon type="info-circle" theme="filled" className="info-icon" />
          </Tooltip>
        )}
      </p>
    </CustomLink>
  </CellsWrapper>
);

const FulfilledCell = (number, workersFulfillment, backup, backupWorkersFulfillment, href) => {
  if (backup) {
    return (
      <CustomLink to={href || ''}>
        <div>
          <p className={fulfilledCssClass({ percent: ifNumberIsNull(number) })}>
            {workersFulfillment} - {ifNumberIsNull(number)}%
          </p>
          <span>|</span>
          <p className={fulfilledCssClass({ percent: backup })}>
            {backupWorkersFulfillment} - {backup}%
          </p>
        </div>
      </CustomLink>
    );
  }

  return (
    <CustomLink to={href || ''}>
      <p className={fulfilledCssClass({ percent: ifNumberIsNull(number) })}>
        {workersFulfillment} - {ifNumberIsNull(number)}%
      </p>
    </CustomLink>
  );
};

const AncestorsCell = (text, ancestors, url) => {
  return TextCellWithHover(text || notAvailable, ancestors && ancestors.length && ancestors.join('  \u25B8  '), url);
};

const TapToCopyCell = (value, component) => {
  return (
    <TapToCopyCellWrapper>
      <CopyToClipboard text={value}>
        <Tooltip title={t('dash.common.tooltips.tap_to_copy_tooltip')}>
          <div role="button" tabIndex="0" className="copy-text-tooltip" onClick={component.copyText}>
            <p className="copy-text-container">{value}</p>
            <Icon type="copy" className="copy-text-icon" />
          </div>
        </Tooltip>
      </CopyToClipboard>
    </TapToCopyCellWrapper>
  );
};

export {
  AncestorsCell,
  IconCell,
  ImageCell,
  LinkCell,
  TextCell,
  TextCellWithHoverMoreTooltip,
  TextCellWithTag,
  IndicatorCell,
  TooltipCell,
  TooltipCellWithHover,
  NumberCell,
  TextCellWithHover,
  CurrencyCell,
  FulfilledCell,
  TapToCopyCell,
  InputDateTimePickerFormItemCell,
  StyledLinkCell,
};
